/**
 * Rechtsformen wie firma_rechtsformen() der PHP-Middleware (api/lib_firma.php).
 *   reg: Registerart als Präfix (HRA, HRB, GnR, PR, GsR, VR, FN), 'frei' = Nummer ohne festes Format, null = kein Register
 *   pflicht: Registerangaben sind Pflicht
 *   vertretung / vertretungPflicht: Bezeichnung der Vertretungsberechtigten
 */
export type Registerart = 'HRA' | 'HRB' | 'GnR' | 'PR' | 'GsR' | 'VR' | 'FN' | 'frei' | null;

export interface Rechtsform {
  titel: string;
  gruppe: string;
  reg: Registerart;
  pflicht: boolean;
  gericht: string;
  gerichtBeispiel: string;
  nr: string;
  nrBeispiel: string;
  vertretung: string;
  vertretungPflicht: boolean;
}

const de = (titel: string, reg: Registerart, vertretung: string, vertretungPflicht: boolean, gruppe = 'Deutschland'): Rechtsform => ({
  titel, gruppe, reg, pflicht: reg !== null,
  gericht: reg === 'VR' ? 'Registergericht (Vereinsregister)' : 'Registergericht', gerichtBeispiel: 'z. B. Amtsgericht Frankfurt am Main',
  nr: 'Registernummer', nrBeispiel: reg ? `z. B. ${reg} 12345` : '', vertretung, vertretungPflicht,
});
const at = (titel: string, vertretung: string): Rechtsform => ({
  titel, gruppe: 'Österreich & Schweiz', reg: 'FN', pflicht: true,
  gericht: 'Firmenbuchgericht', gerichtBeispiel: 'z. B. Handelsgericht Wien', nr: 'Firmenbuchnummer', nrBeispiel: 'z. B. FN 123456a',
  vertretung, vertretungPflicht: true,
});
const int = (titel: string, gericht: string, gerichtBeispiel: string, nr: string, nrBeispiel: string, vertretung: string, pflicht = true, gruppe = 'International'): Rechtsform => ({
  titel, gruppe, reg: 'frei', pflicht, gericht, gerichtBeispiel, nr, nrBeispiel, vertretung, vertretungPflicht: pflicht,
});

export const RECHTSFORMEN = {
  // Deutschland – Unternehmen
  einzel: de('Einzelunternehmen (nicht im Handelsregister)', null, 'Inhaber/in', false),
  ek: de('Eingetragener Kaufmann (e. K.)', 'HRA', 'Inhaber/in', true),
  gbr: de('GbR', null, 'Gesellschafter', false),
  egbr: de('eGbR (eingetragene GbR)', 'GsR', 'Gesellschafter', true),
  ohg: de('OHG', 'HRA', 'Gesellschafter', true),
  kg: de('KG', 'HRA', 'Persönlich haftende Gesellschafter', true),
  gmbhcokg: de('GmbH & Co. KG', 'HRA', 'Geschäftsführer der Komplementär-GmbH', true),
  ugcokg: de('UG (haftungsbeschränkt) & Co. KG', 'HRA', 'Geschäftsführer der Komplementär-UG', true),
  gmbh: de('GmbH', 'HRB', 'Geschäftsführer', true),
  ggmbh: de('gGmbH (gemeinnützige GmbH)', 'HRB', 'Geschäftsführer', true),
  ug: de('UG (haftungsbeschränkt)', 'HRB', 'Geschäftsführer', true),
  ag: de('AG', 'HRB', 'Vorstand', true),
  kgaa: de('KGaA', 'HRB', 'Persönlich haftende Gesellschafter', true),
  se: de('SE (Europäische Gesellschaft)', 'HRB', 'Vorstand bzw. geschäftsführende Direktoren', true),
  eg: de('eG (Genossenschaft)', 'GnR', 'Vorstand', true),
  partg: de('Partnerschaftsgesellschaft (PartG / PartG mbB)', 'PR', 'Partner', true),
  // Deutschland – Vereine, Stiftungen, öffentliche Hand
  ev: de('Eingetragener Verein (e. V.)', 'VR', 'Vorstand', true, 'Vereine & Organisationen'),
  verein: de('Nicht eingetragener Verein', null, 'Vorstand', false, 'Vereine & Organisationen'),
  stiftung: de('Stiftung', null, 'Vorstand', true, 'Vereine & Organisationen'),
  oeffentl: de('Körperschaft / Anstalt des öffentlichen Rechts', null, 'Vertretungsberechtigte', false, 'Vereine & Organisationen'),
  // Österreich & Schweiz
  at_gmbh: at('GmbH (Österreich)', 'Geschäftsführer'),
  at_ag: at('AG (Österreich)', 'Vorstand'),
  at_og: at('OG / KG (Österreich)', 'Gesellschafter'),
  at_eu: at('e. U. (Österreich)', 'Inhaber/in'),
  ch_gmbh: int('GmbH (Schweiz)', 'Handelsregisteramt', 'z. B. Handelsregisteramt des Kantons Zürich', 'UID', 'z. B. CHE-123.456.789', 'Geschäftsführer', true, 'Österreich & Schweiz'),
  ch_ag: int('AG (Schweiz)', 'Handelsregisteramt', 'z. B. Handelsregisteramt des Kantons Zürich', 'UID', 'z. B. CHE-123.456.789', 'Verwaltungsrat', true, 'Österreich & Schweiz'),
  // International
  uk_ltd: int('Ltd – Private Limited Company (Vereinigtes Königreich)', 'Register', 'Companies House', 'Company Number', 'z. B. 12345678', 'Director(s)'),
  uk_plc: int('PLC – Public Limited Company (Vereinigtes Königreich)', 'Register', 'Companies House', 'Company Number', 'z. B. 12345678', 'Director(s)'),
  uk_llp: int('LLP – Limited Liability Partnership (Vereinigtes Königreich)', 'Register', 'Companies House', 'Registration Number', 'z. B. OC123456', 'Designated Members'),
  ie_ltd: int('Ltd / DAC (Irland)', 'Register', 'Companies Registration Office (CRO)', 'Company Number', 'z. B. 123456', 'Director(s)'),
  us_llc: int('LLC – Limited Liability Company (USA)', 'Bundesstaat der Gründung', 'z. B. Delaware', 'File Number / EIN', 'z. B. 1234567', 'Manager / Members', false),
  us_inc: int('Inc. / Corp. (USA)', 'Bundesstaat der Gründung', 'z. B. Delaware', 'File Number / EIN', 'z. B. 1234567', 'Officers (z. B. President, CEO)', false),
  us_llp: int('LLP / LP (USA)', 'Bundesstaat der Gründung', 'z. B. New York', 'File Number / EIN', 'z. B. 1234567', 'Partner', false),
  nl_bv: int('B.V. / N.V. (Niederlande)', 'Register', 'Handelsregister (KvK)', 'KvK-Nummer', 'z. B. 12345678', 'Bestuurder (Geschäftsführung)'),
  be_bv: int('BV / SRL / NV (Belgien)', 'Register', 'Zentrale Unternehmensdatenbank (KBO/BCE)', 'Unternehmensnummer', 'z. B. 0123.456.789', 'Bestuurder / Administrateur'),
  lu_sarl: int('S.à r.l. / S.A. (Luxemburg)', 'Register', 'Registre de commerce (RCS Luxembourg)', 'RCS-Nummer', 'z. B. B123456', 'Gérant / Administrateur'),
  fr_sarl: int('SARL / SAS / SA (Frankreich)', 'Register', 'z. B. RCS Paris', 'SIREN', 'z. B. 123 456 789', 'Gérant / Président'),
  it_srl: int('S.r.l. / S.p.A. (Italien)', 'Register', 'Registro delle Imprese', 'REA / Codice fiscale', 'z. B. MI-1234567', 'Amministratore'),
  es_sl: int('S.L. / S.A. (Spanien)', 'Register', 'Registro Mercantil', 'NIF (CIF)', 'z. B. B12345678', 'Administrador'),
  pl_spzoo: int('sp. z o.o. / S.A. (Polen)', 'Register', 'Krajowy Rejestr Sądowy (KRS)', 'KRS-Nummer', 'z. B. 0000123456', 'Zarząd (Vorstand)'),
  cz_sro: int('s.r.o. / a.s. (Tschechien)', 'Register', 'Obchodní rejstřík', 'IČO', 'z. B. 12345678', 'Jednatel (Geschäftsführer)'),
  dk_aps: int('ApS / A/S (Dänemark)', 'Register', 'Erhvervsstyrelsen (CVR)', 'CVR-Nummer', 'z. B. 12345678', 'Direktion'),
  se_ab: int('AB (Schweden)', 'Register', 'Bolagsverket', 'Organisationsnummer', 'z. B. 556123-4567', 'Styrelse / VD'),
  ausland: int('Andere ausländische Gesellschaft', 'Register / Behörde', 'Name des Registers', 'Registernummer', 'falls vorhanden', 'Vertretungsberechtigte', false),
  sonst: int('Sonstige Rechtsform', 'Register / Behörde', 'falls vorhanden', 'Registernummer', 'falls vorhanden', 'Vertretungsberechtigte', false, 'Sonstige'),
} as const satisfies Record<string, Rechtsform>;

export type RechtsformCode = keyof typeof RECHTSFORMEN;
export const RECHTSFORM_CODES = Object.keys(RECHTSFORMEN) as [RechtsformCode, ...RechtsformCode[]];

export function rechtsformTitel(code: string): string {
  return (RECHTSFORMEN as Record<string, Rechtsform>)[code]?.titel ?? '';
}

const PRAEFIX = ['HRA', 'HRB', 'GnR', 'PR', 'GsR', 'VR', 'FN'];
const SCHREIBWEISE: Record<string, string> = { GNR: 'GnR', GSR: 'GsR' };

/** Registernummer vereinheitlichen: „12345“ → „HRB 12345“, „fn123456A“ → „FN 123456a“ (wie firma_regnr) */
export function registerNrNormalisieren(nr: string, art: Registerart): string {
  const n = nr.replace(/\s+/g, ' ').trim();
  if (!n) return '';
  if (!art || !PRAEFIX.includes(art)) {
    const m = n.match(/^(HRA|HRB|GNR|PR|GSR|VR)\s*(\d.*)$/i);
    return m ? `${SCHREIBWEISE[m[1]!.toUpperCase()] ?? m[1]!.toUpperCase()} ${m[2]!.trim().toUpperCase()}` : n;
  }
  if (art === 'FN') return `FN ${n.replace(/^FN\s*/i, '').toLowerCase()}`;
  const gross = n.toUpperCase();
  const m = gross.match(/^(HRA|HRB|GNR|PR|GSR|VR)\s*(.+)$/);
  return m ? `${SCHREIBWEISE[m[1]!] ?? m[1]} ${m[2]!.trim()}` : `${art} ${gross}`;
}

/** Registerangaben prüfen (wie firma_reg_pruefen) – leerer Text = in Ordnung */
export function registerPruefen(code: string, gericht: string, nr: string): { feld: 'registerGericht' | 'registerNr'; text: string } | null {
  const f = (RECHTSFORMEN as Record<string, Rechtsform>)[code];
  if (!f || f.reg === null || !f.pflicht) return null;
  if (!gericht) return { feld: 'registerGericht', text: `Bitte „${f.gericht}“ angeben` };
  if (!nr) return { feld: 'registerNr', text: `Bitte „${f.nr}“ angeben` };
  if (PRAEFIX.includes(f.reg)) {
    const muster = f.reg === 'FN' ? /^FN \d+\s?[a-z]$/ : new RegExp(`^${f.reg} \\d+`);
    if (!muster.test(nr)) return { feld: 'registerNr', text: `Bitte „${f.nr}“ im Format ${f.nrBeispiel.replace(/^z\. B\. /, '')} angeben` };
  }
  return null;
}
